import moment from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;

export type Status = "Not Started" | "In Progress" | "Completed" | "Overdue";

export interface CheckpointInfo {
  checkpointId?: number;
  name?: string;
  dueDate?: string | moment.Moment | null;
  isCompleted?: boolean;
  completedAt?: string | null;
}

/**
 * 基于任务整体 startDate / dueDate 推导状态（新西兰时区，与「无 checkpoint」时一致）。
 */
const statusFromTaskDates = (
  startDate: moment.Moment | string | null | undefined,
  dueDate: moment.Moment | string | null | undefined,
  now: moment.Moment
): Status => {
  const startDateMoment = startDate
    ? (moment.isMoment(startDate) ? startDate.clone().tz(NZ_TIMEZONE) : toNZTime(startDate))
    : null;
  const dueDateMoment = dueDate
    ? (moment.isMoment(dueDate) ? dueDate.clone().tz(NZ_TIMEZONE) : toNZTime(dueDate))
    : null;

  if (startDateMoment && now.isBefore(startDateMoment, "day")) {
    return "Not Started";
  }

  if (dueDateMoment && now.isAfter(dueDateMoment, "day")) {
    return "Overdue";
  }

  if (startDateMoment && now.isSameOrAfter(startDateMoment, "day")) {
    if (!dueDateMoment || now.isSameOrBefore(dueDateMoment, "day")) {
      return "In Progress";
    }
  }

  if (dueDateMoment && !startDateMoment && now.isSameOrBefore(dueDateMoment, "day")) {
    return "In Progress";
  }

  return "Not Started";
};

/**
 * 根据 Checkpoints 与任务日期计算任务状态（使用新西兰时区）
 * 逻辑：
 * 1. 若原始状态为 Completed，则保持 Completed
 * 2. 若有 checkpoint：找第一个未完成的（按截止日期排序）；按其 dueDate 判断 In Progress / Overdue
 * 3. 若所有 checkpoint 均已完成：不按 Completed 处理，回退为任务整体 startDate / dueDate 逻辑
 * 4. 若无 checkpoint：使用任务整体日期逻辑
 */
export const calculateStatus = (
  originalStatus: Status | null | undefined,
  startDate: moment.Moment | string | null | undefined,
  dueDate: moment.Moment | string | null | undefined,
  checkpoints?: CheckpointInfo[] | null
): Status => {
  // 如果原始状态是 Completed，则永久保持 Completed
  if (originalStatus === "Completed") {
    return "Completed";
  }

  const now = moment().tz(NZ_TIMEZONE);

  // 如果有 checkpoints，使用新逻辑
  if (checkpoints && checkpoints.length > 0) {
    // 按截止日期排序（最早的在前）
    const sortedCheckpoints = [...checkpoints].sort((a, b) => {
      const dateA = a.dueDate ? toNZTime(a.dueDate) : null;
      const dateB = b.dueDate ? toNZTime(b.dueDate) : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.valueOf() - dateB.valueOf();
    });

    // 找到第一个未完成的 checkpoint
    const firstIncompleteCheckpoint = sortedCheckpoints.find(cp => !cp.isCompleted);

    // 如果所有 checkpoint 都完成了：按任务本身日期显示，不强制 Completed
    if (!firstIncompleteCheckpoint) {
      return statusFromTaskDates(startDate, dueDate, now);
    }

    // 检查第一个未完成的 checkpoint 的截止日期
    const cpDueDate = firstIncompleteCheckpoint.dueDate
      ? toNZTime(firstIncompleteCheckpoint.dueDate)
      : null;

    if (cpDueDate) {
      if (now.isAfter(cpDueDate, "day")) {
        return "Overdue";
      }
      return "In Progress";
    }

    return "In Progress";
  }

  return statusFromTaskDates(startDate, dueDate, now);
};

/**
 * 获取状态对应的颜色
 * @param status 任务状态
 * @returns 颜色值（十六进制）
 */
export const getStatusColor = (status: Status | null | undefined): string => {
  const STATUS_COLOR_MAP: Record<Status, string> = {
    "Not Started": "#000000", // 黑色
    "In Progress": "#52c41a",
    "Completed": "#000000",
    "Overdue": "#f5222d",
  };

  if (!status) {
    return STATUS_COLOR_MAP["Not Started"];
  }

  return STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP["Not Started"];
};
